<script setup>
import { ActionEnum, TypeDataEnum, TypeSysConfigEnum } from "@/utilities/enum";
</script>

<template>
  <DefaultLayout class="!bg-slate-50">
    <v-row class="mx-5 mt-2 font-inter">
      <v-col cols="12">
        <BreadcrumbComponent
          :items="[
            { label: 'Master Data' },
            { label: 'Bahan Bakar', active: true },
          ]"
        />
        <h2 class="font-bold text-2xl text-[#232323] font-inter">
          Master Data - Bahan Bakar
        </h2>
      </v-col>
      <v-col cols="12" class="bg-white shadow-md p-3 rounded-md mb-10">
        <div class="flex gap-3">
          <v-btn
            variant="outlined"
            class="capitalize font-medium text-xs w-44 border-2"
            :class="[
              tabActive === TypeDataEnum.EQUIPMENT
                ? 'text-red-600 border-red-600'
                : 'text-slate-600 border-slate-600',
              isEdit ? 'cursor-not-allowed' : 'cursor-pointer',
            ]"
            @click="!isEdit ? (tabActive = TypeDataEnum.EQUIPMENT) : ''"
          >
            Jenis Peralatan
          </v-btn>
          <v-btn
            variant="outlined"
            class="capitalize font-medium text-xs w-44 border-2"
            :class="[
              tabActive === TypeDataEnum.FUEL
                ? 'text-red-600 border-red-600'
                : 'text-slate-600 border-slate-600',
              isEdit ? 'cursor-not-allowed' : 'cursor-pointer',
            ]"
            @click="!isEdit ? (tabActive = TypeDataEnum.FUEL) : ''"
          >
            Jenis Bahan Bakar
          </v-btn>
        </div>
        <hr class="my-3 border-t-2 border-dashed border-gray-500" />
        <h2 class="text-lg font-medium text-slate-800">
          Jenis
          {{
            tabActive === TypeDataEnum.EQUIPMENT
              ? "Peralatan (" + existing.equipment.totalItems + ")"
              : "Bahan Bakar (" + existing.fuel.totalItems + ")"
          }}
        </h2>
        <div class="flex justify-between items-center mt-3" v-if="!isEdit">
          <div class="flex gap-3 items-center w-full">
            <v-text-field
              variant="outlined"
              density="compact"
              hide-details
              clearable
              v-model="keywordModel"
              color="#475569"
              prepend-inner-icon="mdi-magnify"
              placeholder="Cari dengan nama"
              class="w-64 max-w-64"
              @keyup.enter="
                tabActive === TypeDataEnum.EQUIPMENT
                  ? fetchDataEquipment()
                  : fetchDataFuel()
              "
              @click:clear="
                tabActive === TypeDataEnum.EQUIPMENT
                  ? fetchDataEquipment()
                  : fetchDataFuel()
              "
            >
            </v-text-field>
            <v-autocomplete
              v-if="tabActive === TypeDataEnum.EQUIPMENT"
              variant="outlined"
              hide-details
              color="#475569"
              v-model="equipment.type"
              :items="typeEquipment"
              item-title="title"
              item-value="value"
              density="compact"
              placeholder="Emisi"
              class="w-40 max-w-40"
              @update:model-value="fetchDataEquipment()"
            >
            </v-autocomplete>
            <v-autocomplete
              variant="outlined"
              hide-details
              color="#475569"
              v-model="activeModel"
              item-title="title"
              item-value="value"
              :items="[
                { title: 'Semua Status', value: '' },
                { title: 'Active', value: true },
                { title: 'Inactive', value: false },
              ]"
              density="compact"
              placeholder="Semua Status"
              class="w-40 max-w-40"
              @update:model-value="
                tabActive === TypeDataEnum.EQUIPMENT
                  ? fetchDataEquipment()
                  : fetchDataFuel()
              "
            >
            </v-autocomplete>
          </div>
          <v-btn
            class="capitalize text-xs min-w-40 w-40 bg-gray-800"
            @click="
              tabActive === TypeDataEnum.EQUIPMENT
                ? getAllDataEquipment()
                : getAllDataFuel()
            "
          >
            <span class="text-white text-sm">Tambah/Edit</span>
            <template v-slot:prepend>
              <img
                src="../../../assets/icon/edit.png"
                alt="icon"
                class="w-5 h-auto"
                loading="lazy"
              />
            </template>
          </v-btn>
        </div>
        <div class="flex justify-between items-center mt-3" v-else>
          <v-btn
            class="min-w-40 w-40 border-gray-500 bg-white"
            variant="outlined"
            @click="
              tabActive === TypeDataEnum.EQUIPMENT
                ? addRowEquipment()
                : addRowFuel()
            "
          >
            <span class="capitalize text-gray-600 text-sm">Tambah Data</span>
            <template v-slot:prepend>
              <img
                src="../../../assets/icon/add-square-white.png"
                alt="icon"
                class="w-5 h-auto"
                loading="lazy"
              />
            </template>
          </v-btn>
          <div class="flex gap-3 items-center justify-end w-full">
            <v-btn
              class="min-w-40 w-40 border-gray-500 bg-white"
              variant="outlined"
              @click="
                tabActive === TypeDataEnum.EQUIPMENT
                  ? cancelEditEquipment()
                  : cancelEditFuel()
              "
            >
              <span class="capitalize text-gray-600 text-sm">Batal</span>
            </v-btn>
            <v-btn
              variant="text"
              class="min-w-40 w-40 bg-red-600"
              id="btn-submit"
              @click="
                tabActive === TypeDataEnum.EQUIPMENT
                  ? submitEquipment()
                  : submitFuel()
              "
            >
              <span class="capitalize text-white text-sm font-medium">
                Submit
              </span>
              <img
                src="../../../assets/icon/send.png"
                alt="Submit Icon"
                class="ml-2 h-4 w-auto"
              />
            </v-btn>
          </div>
        </div>
        <v-form ref="formData" class="mt-4">
          <EquipmentTableComponent
            v-if="tabActive === TypeDataEnum.EQUIPMENT"
            v-model:dataTable="dataTableEquipment"
            :existing="existing.equipment"
            :form-validasi="formValidasiEquipment"
            :isEdit="isEdit"
            :add-row="addRowEquipment"
            :remove-row="removeRowEquipment"
            :fetch-data="fetchDataEquipment"
          />
          <FuelTableComponent
            v-if="tabActive === TypeDataEnum.FUEL"
            v-model:dataTable="dataTableFuel"
            :existing="existing.fuel"
            :form-validasi="formValidasiFuel"
            :is-edit="isEdit"
            :settings="sysSettingData"
            :add-row="addRowFuel"
            :remove-row="removeRowFuel"
            :fetch-data="fetchDataFuel"
            :sys-config="sysConfig"
          />
        </v-form>
      </v-col>
    </v-row>
  </DefaultLayout>
  <ModalDelete
    v-model="dialogDelete"
    @close-modal="closeModal"
    @delete-data="deleteData"
  />
  <ModalValidation
    v-model="dialogValidation"
    :data="errorMessages"
    @close-modal="closeModal"
  />
  <SysConfigModal
    v-model="dialogSysConfig"
    :form-validasi-sys-config="formValidasiSysConfig"
    :data="sysConfigData"
    :settings="sysSettingData"
    @close-modal="closeModal"
    @submit-data="submitSysConfig"
  />
</template>

<script>
import DefaultLayout from "@/layouts/DefaultLayout.vue";
import EquipmentTableComponent from "./components/EquipmentTableComponent.vue";
import { CreateDataTableObject } from "@/utilities/DataTableHelpers";
import {
  AsignFormDataSysConfig,
  FormCreateDataEquipment,
  FormCreateDataFuel,
  FormValidasiDataEquipment,
  FormValidasiDataFuel,
  FormValidasiDataSysConfig,
} from "./core/FormData";
import { cloneDeep } from "lodash";
import util from "@/assets/js/util";
import axios from "axios";
import { APP_BACKEND_ESG } from "@/constants";
import ModalDelete from "@/components/ModalDelete.vue";
import ModalValidation from "../components/ModalValidation.vue";
import FuelTableComponent from "./components/FuelTableComponent.vue";
import typeEquipmentData from "@/data/typeEquipment.json";
import SysConfigModal from "./components/SysConfigModal.vue";
import BreadcrumbComponent from "@/components/BreadcrumbComponent.vue";

const base_url = APP_BACKEND_ESG;

export default {
  name: "MasterDataFuelPage",
  components: {
    DefaultLayout,
    EquipmentTableComponent,
    FuelTableComponent,
    ModalDelete,
    ModalValidation,
    SysConfigModal,
    BreadcrumbComponent,
  },
  data() {
    return {
      dataTableEquipment: CreateDataTableObject([
        { title: "", align: "start", width: "5%" },
        { title: "Nama Peralatan", key: "name", align: "start", width: "30%" },
        { title: "Jenis Emisi", key: "type", align: "start", width: "30%" },
        { title: "Status", align: "start", width: "30%" },
        { title: "", align: "start", width: "5%" },
      ]),
      dataTableFuel: CreateDataTableObject([
        { title: "", align: "center", width: "5%" },
        {
          title: "Nama Bahan Bakar",
          key: "name",
          align: "start",
          width: "35%",
        },
        {
          title: "Konfigurasi",
          align: "start",
          width: "25%",
        },
        { title: "Status", align: "start", width: "30%" },
        { title: "", align: "center", width: "5%" },
      ]),
      existing: {
        equipment: {
          items: [],
          totalItems: 0,
          perPage: 10,
          page: 1,
          loading: false,
          sortBy: null,
        },
        fuel: {
          items: [],
          totalItems: 0,
          perPage: 10,
          page: 1,
          loading: false,
          sortBy: null,
        },
      },
      isEdit: false,
      tabActive: TypeDataEnum.EQUIPMENT,
      formValidasiEquipment: cloneDeep(FormValidasiDataEquipment),
      formValidasiFuel: cloneDeep(FormValidasiDataFuel),
      formValidasiSysConfig: cloneDeep(FormValidasiDataSysConfig),
      dialogValidation: false,
      dialogDelete: false,
      dialogSysConfig: false,
      deleteId: null,
      errorMessages: [],
      typeEquipment: [
        { title: "Semua Emisi", value: "" },
        ...typeEquipmentData,
      ],
      equipment: {
        keyword: null,
        type: "",
        active: "",
      },
      fuel: {
        keyword: null,
        active: "",
      },
      sysConfigData: {},
      sysSettingData: {},
    };
  },
  mounted() {
    this.sysSetting();
  },
  watch: {
    "dataTableEquipment.items": {
      handler(newItems) {
        newItems.forEach((item) => {
          const index = item.existing_index;

          if (index !== undefined && this.existing.equipment.items[index]) {
            const newItem = { ...item };
            delete newItem.existing_index;
            this.existing.equipment.items.splice(index, 1, newItem);
          }
        });

        const keyMap = new Map();

        this.existing.equipment.items
          .filter((data) => {
            return !data.deleted && data.name;
          })
          .forEach((item, index) => {
            let name = item.name.toLowerCase();
            let key = `${name}`;

            if (!keyMap.has(key)) {
              keyMap.set(key, []);
            }
            keyMap.get(key).push(index);
          });

        this.existing.equipment.items
          .filter((data) => {
            return !data.deleted && data.name;
          })
          .forEach((item) => {
            let name = item.name.toLowerCase();
            let key = `${name}`;

            const duplicateIndexes = keyMap.get(key) || [];
            item.duplicate = duplicateIndexes.length > 1;
          });
      },
      deep: true,
    },
    "dataTableFuel.items": {
      handler(newItems) {
        newItems.forEach((item) => {
          const index = item.existing_index;

          if (index !== undefined && this.existing.fuel.items[index]) {
            const newItem = { ...item };
            delete newItem.existing_index;
            this.existing.fuel.items.splice(index, 1, newItem);
          }

          const keyMap = new Map();

          this.existing.fuel.items
            .filter((data) => {
              return !data.deleted && data.name;
            })
            .forEach((item, index) => {
              let key = item.name.toLowerCase();

              if (!keyMap.has(key)) {
                keyMap.set(key, []);
              }
              keyMap.get(key).push(index);
            });

          this.existing.fuel.items
            .filter((data) => {
              return !data.deleted && data.name;
            })
            .forEach((item) => {
              let key = item.name.toLowerCase();

              const duplicateIndexes = keyMap.get(key) || [];
              item.duplicate = duplicateIndexes.length > 1;
            });
        });
      },
      deep: true,
    },
  },
  methods: {
    addRowEquipment() {
      const formData = cloneDeep(FormCreateDataEquipment);
      Object.assign(formData, { uuid: Date.now() + Math.random() });

      this.existing.equipment.items.unshift(formData);
      this.existing.equipment.totalItems = this.existing.equipment.items.filter(
        (item) => !item.deleted,
      ).length;

      this.existing.equipment.page = 1;

      const perPage = this.existing.equipment.perPage;
      const startIndex = 0;

      this.dataTableEquipment.items = this.existing.equipment.items
        .map((item, index) => ({ ...item, existing_index: index }))
        .filter((item) => !item.deleted)
        .slice(startIndex, startIndex + perPage);

      this.dataTableEquipment.start_index = startIndex;
    },
    removeRowEquipment(existing_index) {
      if (this.isEdit) {
        const page = this.existing.equipment.page;
        const perPage = this.existing.equipment.perPage;
        this.existing.equipment.items = this.existing.equipment.items.map(
          (item, index) => {
            if (index === existing_index) {
              item.deleted = true;
              item.is_touched = true;
            }
            return item;
          },
        );

        this.existing.equipment.totalItems =
          this.existing.equipment.items.filter((item) => !item.deleted)
            .length ?? 0;

        this.dataTableEquipment.items = this.existing.equipment.items
          .map((item, index) => ({ ...item, existing_index: index }))
          .filter((item) => !item.deleted)
          .slice((page - 1) * perPage, page * perPage);
      } else {
        this.deleteId = this.dataTableEquipment.items.find(
          (item) => item.existing_index === existing_index,
        ).id;
        this.dialogDelete = true;
      }
    },
    async fetchDataEquipment({ page, itemsPerPage, sortBy } = {}) {
      if (this.isEdit) {
        this.existing.equipment.page = page ?? 1;
        this.existing.equipment.perPage = itemsPerPage ?? 10;
        if (sortBy && sortBy[0]) {
          if (sortBy && sortBy[0]) {
            const { key, order } = sortBy[0];
            this.existing.equipment.items.sort((a, b) => {
              const valA = (a[key] ?? "").toString().toLowerCase();
              const valB = (b[key] ?? "").toString().toLowerCase();

              if (valA < valB) return order === "asc" ? -1 : 1;
              if (valA > valB) return order === "asc" ? 1 : -1;
              return 0;
            });
          }
        }

        const startIndex = (page - 1) * itemsPerPage;

        this.dataTableEquipment.items = this.existing.equipment.items
          .map((item, index) => ({ ...item, existing_index: index }))
          .filter((item) => !item.deleted)
          .slice(startIndex, startIndex + itemsPerPage);

        this.dataTableEquipment.start_index = startIndex;
      } else {
        try {
          this.existing.equipment.loading = true;
          const res = await axios.get(`${base_url}/equipment`, {
            params: {
              name: this.equipment.keyword?.trim(),
              type: this.equipment.type,
              active: this.equipment.active,
              page: page,
              size: itemsPerPage,
              sortBy: sortBy ? sortBy[0] : null,
            },
          });

          const pageData = res.data.current_page || 1;
          const perPageData = res.data.total_per_page;
          const startIndex = (pageData - 1) * perPageData;

          this.existing.equipment.page = pageData;
          this.existing.equipment.perPage = perPageData;
          this.existing.equipment.totalItems = res.data.total_data;
          this.existing.equipment.sortBy = sortBy ? sortBy[0] : null;

          this.dataTableEquipment.items = res.data.data.map((item, index) => ({
            ...item,
            name_touched: false,
            type_touched: false,
            existing_index: index,
          }));
          this.dataTableEquipment.start_index = startIndex;
        } catch (err) {
          this.existing.equipment.loading = false;
          util.showError(err);
        } finally {
          this.existing.equipment.loading = false;
        }
      }
    },
    async getAllDataEquipment() {
      try {
        this.existing.equipment.loading = true;
        this.isEdit = true;
        const res = await axios.get(`${base_url}/equipment`, {
          params: {
            name: this.equipment.keyword?.trim(),
            type: this.equipment.type,
            page: this.existing.equipment.page,
            size: -1,
            sortBy: this.existing.equipment.sortBy,
          },
        });

        this.existing.equipment.items =
          res.data.data.map((item) => ({
            ...item,
            uuid: Date.now() + Math.random(),
            name_touched: false,
            type_touched: false,
            deleted: false,
            duplicate: false,
            is_touched: false,
          })) || [];
        this.existing.equipment.totalItems = res.data.total_data || 0;

        const page = this.existing.equipment.page;
        const perPage = this.existing.equipment.perPage;
        const startIndex = (page - 1) * perPage;

        this.dataTableEquipment.items = this.existing.equipment.items
          .map((item, index) => ({ ...item, existing_index: index }))
          .slice(startIndex, startIndex + perPage);
        this.dataTableEquipment.start_index = startIndex;
      } catch (err) {
        this.isEdit = false;
        this.existing.equipment.loading = false;
        util.showError(err);
      } finally {
        this.existing.equipment.loading = false;
      }
    },
    async submitEquipment() {
      const formData = await this.$refs.formData.validate();
      const items = this.existing.equipment.items.filter(
        (item) => !item.deleted,
      );
      const itemsValid = items.every((item) => item.name && item.type);
      const itemsDuplicate = this.existing.equipment.items.some(
        (item) => item.duplicate && !item.deleted,
      );

      if (formData.valid && itemsValid && !itemsDuplicate) {
        let bodyJson = this.existing.equipment.items
          .filter(
            (item) =>
              (item.id || (!item.id && !item.deleted)) && item.is_touched,
          )
          .map((data) => ({
            id: data.id,
            name: data.name,
            type: data.type,
            active: data.active,
            action: !data.id
              ? ActionEnum.CREATE
              : data.deleted
                ? ActionEnum.DELETE
                : ActionEnum.UPDATE,
          }));
        try {
          const res = await axios.post(`${base_url}/equipment`, bodyJson);

          util.showInfo(res.data.message);
        } catch (err) {
          util.showError(err);
        } finally {
          this.isEdit = false;
          this.fetchDataEquipment();
        }
      } else {
        let message = [];
        if (!formData.valid) {
          message.push(
            ...formData.errors
              .map((data) => {
                const uuid = data.id.split("_")[1];

                const index = this.dataTableEquipment.items.findIndex(
                  (item) => item.uuid && item.uuid === uuid,
                );

                if (index !== -1) {
                  if (data.id.startsWith("name_")) {
                    this.dataTableEquipment.items[index].name_touched = true;
                  }
                  if (data.id.startsWith("type_")) {
                    this.dataTableEquipment.items[index].type_touched = true;
                  }

                  message.push(
                    `Item ke-${index + 1}: ${data.errorMessages[0]}`,
                  );
                }

                return null;
              })
              .filter(Boolean),
          );
        }

        if (!itemsValid) {
          const itemRules = (item) => [
            () => (item.name ? true : "Nama peralatan harus diisi"),
            () => (item.type ? true : "Emisi tidak valid"),
          ];

          const data = this.existing.equipment.items.filter(
            (item) => !item.deleted,
          );

          const validationResults = await Promise.all(
            data.flatMap((item, index) =>
              itemRules(item).map((rule) => {
                const result = rule();
                return result instanceof Promise
                  ? result.then((res) => ({ res, index }))
                  : { res: result, index };
              }),
            ),
          );

          const errors = validationResults.filter((v) => v.res !== true);

          if (errors.length > 0) {
            message.push(
              ...errors.map((err) => {
                return `Item ke-${err.index + 1}: ${err.res}`;
              }),
            );
          }
        }

        if (itemsDuplicate) {
          const data = this.existing.equipment.items.filter(
            (item) => !item.deleted,
          );

          const duplicates = data
            .map((item, i) => ({ ...item, index: i }))
            .filter((item) => item.duplicate);

          duplicates.forEach((item) => {
            message.push(
              `Item ke-${item.index + 1}: Peralatan tidak boleh sama.`,
            );
          });
        }

        const getNumber = (str) => {
          const match = str.match(/Item ke-(\d+)/);
          return match ? parseInt(match[1]) : Infinity;
        };

        const uniqueMap = new Map();

        message.forEach((item) => {
          if (!uniqueMap.has(item)) {
            uniqueMap.set(item, item);
          }
        });

        message = Array.from(uniqueMap.values());

        message = message.sort((a, b) => getNumber(a) - getNumber(b));

        this.errorMessages = message;
        this.dialogValidation = true;
      }
    },
    cancelEditEquipment() {
      let options = {
        page: this.existing.equipment.page,
        itemsPerPage: this.existing.equipment.perPage,
        sortBy: null,
      };

      this.isEdit = false;
      this.fetchDataEquipment(options);
    },
    addRowFuel() {
      const formData = cloneDeep(FormCreateDataFuel);
      Object.assign(formData, { uuid: Date.now() + Math.random() });

      this.existing.fuel.items.unshift(formData);
      this.existing.fuel.totalItems = this.existing.fuel.items.filter(
        (item) => !item.deleted,
      ).length;

      this.existing.fuel.page = 1;

      const perPage = this.existing.fuel.perPage;
      const startIndex = 0;

      this.dataTableFuel.items = this.existing.fuel.items
        .map((item, index) => ({ ...item, existing_index: index }))
        .filter((item) => !item.deleted)
        .slice(startIndex, startIndex + perPage);

      this.dataTableFuel.start_index = startIndex;
    },
    removeRowFuel(existing_index) {
      if (this.isEdit) {
        const page = this.existing.fuel.page;
        const perPage = this.existing.fuel.perPage;
        this.existing.fuel.items = this.existing.fuel.items.map(
          (item, index) => {
            if (index === existing_index) {
              item.deleted = true;
              item.is_touched = true;
            }
            return item;
          },
        );

        this.existing.fuel.totalItems =
          this.existing.fuel.items.filter((item) => !item.deleted).length ?? 0;

        this.dataTableFuel.items = this.existing.fuel.items
          .map((item, index) => ({ ...item, existing_index: index }))
          .filter((item) => !item.deleted)
          .slice((page - 1) * perPage, page * perPage);
      } else {
        this.deleteId = this.dataTableFuel.items.find(
          (item) => item.existing_index === existing_index,
        ).id;
        this.dialogDelete = true;
      }
    },
    async fetchDataFuel({ page, itemsPerPage, sortBy } = {}) {
      if (this.isEdit) {
        this.existing.fuel.page = page ?? 1;
        this.existing.fuel.perPage = itemsPerPage ?? 10;
        if (sortBy && sortBy[0]) {
          if (sortBy && sortBy[0]) {
            const { key, order } = sortBy[0];
            this.existing.fuel.items.sort((a, b) => {
              const valA = (a[key] ?? "").toString().toLowerCase();
              const valB = (b[key] ?? "").toString().toLowerCase();

              if (valA < valB) return order === "asc" ? -1 : 1;
              if (valA > valB) return order === "asc" ? 1 : -1;
              return 0;
            });
          }
        }

        const startIndex = (page - 1) * itemsPerPage;

        this.dataTableFuel.items = this.existing.fuel.items
          .map((item, index) => ({ ...item, existing_index: index }))
          .filter((item) => !item.deleted)
          .slice(startIndex, startIndex + itemsPerPage);

        this.dataTableFuel.start_index = startIndex;
      } else {
        try {
          this.existing.fuel.loading = true;
          const res = await axios.get(`${base_url}/fuel`, {
            params: {
              name: this.fuel.keyword?.trim(),
              active: this.fuel.active,
              page: page,
              size: itemsPerPage,
              sortBy: sortBy ? sortBy[0] : null,
            },
          });

          const pageData = res.data.current_page || 1;
          const perPageData = res.data.total_per_page;
          const startIndex = (pageData - 1) * perPageData;

          this.existing.fuel.page = pageData;
          this.existing.fuel.perPage = perPageData;
          this.existing.fuel.totalItems = res.data.total_data;
          this.existing.fuel.sortBy = sortBy ? sortBy[0] : null;

          this.dataTableFuel.items = res.data.data.map((item, index) => ({
            ...item,
            name_touched: false,
            type_touched: false,
            existing_index: index,
          }));
          this.dataTableFuel.start_index = startIndex;
        } catch (err) {
          this.existing.fuel.loading = false;
          util.showError(err);
        } finally {
          this.existing.fuel.loading = false;
        }
      }
    },
    async getAllDataFuel() {
      try {
        this.existing.fuel.loading = true;
        this.isEdit = true;
        const res = await axios.get(`${base_url}/fuel`, {
          params: {
            name: this.fuel.keyword?.trim(),
            active: this.fuel.active,
            page: this.existing.fuel.page,
            size: -1,
            sortBy: this.existing.fuel.sortBy,
          },
        });

        this.existing.fuel.items =
          res.data.data.map((item) => ({
            ...item,
            uuid: Date.now() + Math.random(),
            name_touched: false,
            type_touched: false,
            deleted: false,
            duplicate: false,
            is_touched: false,
          })) || [];
        this.existing.fuel.totalItems = res.data.total_data || 0;

        const page = this.existing.fuel.page;
        const perPage = this.existing.fuel.perPage;
        const startIndex = (page - 1) * perPage;

        this.dataTableFuel.items = this.existing.fuel.items
          .map((item, index) => ({ ...item, existing_index: index }))
          .slice(startIndex, startIndex + perPage);
        this.dataTableFuel.start_index = startIndex;
      } catch (err) {
        this.isEdit = false;
        this.existing.fuel.loading = false;
        util.showError(err);
      } finally {
        this.existing.fuel.loading = false;
      }
    },
    async submitFuel() {
      const formData = await this.$refs.formData.validate();
      const items = this.existing.fuel.items.filter((item) => !item.deleted);
      const itemsValid = items.every((item) => item.name);
      const itemsDuplicate = this.existing.fuel.items.some(
        (item) => item.duplicate && !item.deleted,
      );

      if (formData.valid && itemsValid && !itemsDuplicate) {
        let bodyJson = this.existing.fuel.items
          .filter(
            (item) =>
              (item.id || (!item.id && !item.deleted)) && item.is_touched,
          )
          .map((data) => ({
            id: data.id,
            name: data.name,
            active: data.active,
            action: !data.id
              ? ActionEnum.CREATE
              : data.deleted
                ? ActionEnum.DELETE
                : ActionEnum.UPDATE,
          }));
        try {
          const res = await axios.post(`${base_url}/fuel`, bodyJson);

          util.showInfo(res.data.message);
        } catch (err) {
          util.showError(err);
        } finally {
          this.isEdit = false;
          this.fetchDataFuel();
        }
      } else {
        let message = [];
        if (!formData.valid) {
          message.push(
            ...formData.errors
              .map((data) => {
                const uuid = data.id.split("_")[1];

                const index = this.dataTableFuel.items.findIndex(
                  (item) => item.uuid && item.uuid === uuid,
                );

                if (index !== -1) {
                  if (data.id.startsWith("name_")) {
                    this.dataTableFuel.items[index].name_touched = true;
                  }

                  message.push(
                    `Item ke-${index + 1}: ${data.errorMessages[0]}`,
                  );
                }

                return null;
              })
              .filter(Boolean),
          );
        }

        if (!itemsValid) {
          const itemRules = (item) => [
            () => (item.name ? true : "Nama bahan bakar harus diisi"),
          ];

          const data = this.existing.fuel.items.filter((item) => !item.deleted);

          const validationResults = await Promise.all(
            data.flatMap((item, index) =>
              itemRules(item).map((rule) => {
                const result = rule();
                return result instanceof Promise
                  ? result.then((res) => ({ res, index }))
                  : { res: result, index };
              }),
            ),
          );

          const errors = validationResults.filter((v) => v.res !== true);

          if (errors.length > 0) {
            message.push(
              ...errors.map((err) => {
                return `Item ke-${err.index + 1}: ${err.res}`;
              }),
            );
          }
        }

        if (itemsDuplicate) {
          const data = this.existing.fuel.items.filter((item) => !item.deleted);

          const duplicates = data
            .map((item, i) => ({ ...item, index: i }))
            .filter((item) => item.duplicate);

          duplicates.forEach((item) => {
            message.push(
              `Item ke-${item.index + 1}: Nama bahan bakar tidak boleh sama.`,
            );
          });
        }

        const getNumber = (str) => {
          const match = str.match(/Item ke-(\d+)/);
          return match ? parseInt(match[1]) : Infinity;
        };

        const uniqueMap = new Map();

        message.forEach((item) => {
          if (!uniqueMap.has(item)) {
            uniqueMap.set(item, item);
          }
        });

        message = Array.from(uniqueMap.values());

        message = message.sort((a, b) => getNumber(a) - getNumber(b));

        this.errorMessages = message;
        this.dialogValidation = true;
      }
    },
    cancelEditFuel() {
      let options = {
        page: this.existing.fuel.page,
        itemsPerPage: this.existing.fuel.perPage,
        sortBy: null,
      };

      this.isEdit = false;
      this.fetchDataFuel(options);
    },
    closeModal() {
      this.deleteId = null;
      this.dialogDelete = false;
      this.dialogValidation = false;
      this.dialogSysConfig = false;

      this.errorMessages = [];
      this.sysConfigData = {};
    },
    async deleteData() {
      let url;

      if (this.tabActive === TypeDataEnum.EQUIPMENT) {
        url = base_url + "/equipment";
      } else if (this.tabActive === TypeDataEnum.FUEL) {
        url = base_url + "/fuel";
      }

      try {
        const res = await axios.delete(url, {
          data: {
            id: this.deleteId,
          },
        });

        util.showInfo(res.data.message);
      } catch (err) {
        util.showError(err);
      } finally {
        this.closeModal();
        if (this.tabActive === TypeDataEnum.EQUIPMENT) {
          this.fetchDataEquipment();
        } else if (this.tabActive === TypeDataEnum.FUEL) {
          this.fetchDataFuel();
        }
      }
    },
    async sysSetting() {
      try {
        const res = await axios.get(`${base_url}/sys-setting`);
        this.sysSettingData = res.data.data;
      } catch (err) {
        util.showError(err);
      }
    },
    async sysConfig(type, title, id, id_fuel) {
      let url;
      if (type === TypeSysConfigEnum.CO2) {
        url = `${base_url}/sys-config-co2`;
      } else if (type === TypeSysConfigEnum.N2O) {
        url = `${base_url}/sys-config-n2o`;
      } else if (type === TypeSysConfigEnum.CH4) {
        url = `${base_url}/sys-config-ch4`;
      }

      if (url) {
        try {
          if (id) {
            const res = await axios.get(`${url}/detail/${id}`);

            this.sysConfigData = AsignFormDataSysConfig(
              this.sysConfigData,
              res.data.data,
            );
          } else {
            this.sysConfigData = AsignFormDataSysConfig(this.sysConfigData, {});
            this.sysConfigData.id_fuel = id_fuel;
          }

          this.sysConfigData.id = id;
          this.sysConfigData.type = type;
          this.sysConfigData.title = title;

          this.dialogSysConfig = true;
        } catch (err) {
          util.showError(err);
        }
      }
    },
    async submitSysConfig(url, formData, id) {
      try {
        let res;

        delete formData.id;
        delete formData.type;
        delete formData.title;

        if (id) {
          res = await axios.put(`${url}/${id}`, formData);
        } else {
          res = await axios.post(`${url}`, formData);
        }

        util.showInfo(res.data.message);

        this.closeModal();
      } catch (err) {
        util.showError(err);
      } finally {
        this.fetchDataFuel();
      }
    },
  },
  computed: {
    keywordModel: {
      get() {
        return this.tabActive === TypeDataEnum.EQUIPMENT
          ? this.equipment.keyword
          : this.fuel.keyword;
      },
      set(val) {
        if (this.tabActive === TypeDataEnum.EQUIPMENT) {
          this.equipment.keyword = val;
        } else {
          this.fuel.keyword = val;
        }
      },
    },
    activeModel: {
      get() {
        return this.tabActive === TypeDataEnum.EQUIPMENT
          ? this.equipment.active
          : this.fuel.active;
      },
      set(val) {
        if (this.tabActive === TypeDataEnum.EQUIPMENT) {
          this.equipment.active = val;
        } else {
          this.fuel.active = val;
        }
      },
    },
  },
};
</script>

<style scoped>
:deep(thead) {
  background-color: #f0f1f4;
  font-weight: 500;
  font-size: 13px;
  color: #232323;
}

:deep(.v-table > .v-table__wrapper > table > tbody > tr > td),
:deep(.v-table > .v-table__wrapper > table > thead > tr > td),
:deep(.v-table > .v-table__wrapper > table > thead > tr > th) {
  padding: 0px 8px;
}
</style>
